/** UI translation bundles — not long-form content. */

import {
  LANG_LABELS,
  normalizePreferredLang,
  TRI_PREFERRED_LANG_CODES,
} from "@/lib/i18n/tri-language";

/** UI chrome languages (nav labels). Content meanings use TRI_PREFERRED_LANG_CODES. */
export const SUPPORTED_LANGS = [
  { code: "en", label: LANG_LABELS.en },
  { code: "hi", label: LANG_LABELS.hi },
  { code: "ta", label: LANG_LABELS.ta },
  { code: "te", label: LANG_LABELS.te },
  { code: "kn", label: LANG_LABELS.kn },
  { code: "ml", label: LANG_LABELS.ml },
  { code: "sa", label: LANG_LABELS.sa },
  ...TRI_PREFERRED_LANG_CODES.filter(
    (c) => !["hi", "ta", "te", "kn", "ml"].includes(c)
  ).map((code) => ({ code, label: LANG_LABELS[code] || code.toUpperCase() })),
];

const en = {
  "nav.knowledge": "Knowledge Base",
  "nav.library": "Sacred Library",
  "nav.panchanga": "Pañchāṅga",
  "nav.prayer_hall": "Prayer Hall",
  "nav.satsanga": "Satsaṅga",
  "nav.sadhana": "Sādhana",
  "nav.diagnostic": "Diagnostic",
  "nav.acharya": "Āchārya AI",
  "nav.dashboard": "Dashboard",
  "nav.foundations": "Foundations",
  "nav.lifestyle": "Lifestyle",
  "nav.streams": "Streams",
  "nav.parampara": "Paramparā",
  "nav.crisis": "Crisis Guidance",
  "nav.gates": "12 Gates",
  "nav.adhikara": "4 Adhikāra",
  "nav.pathways": "4 Pathways",
  "nav.curriculum": "Curriculum",
  "nav.dharma_decisions": "Dharma Decisions",
  "nav.help_now": "Help now",
  "nav.journal": "Journal",
  "nav.knowledge_overview": "Overview",
  "nav.dharma_decisions": "Dharma Decisions",
  "nav.teacher": "Teacher",
  "nav.sign_in": "Sign In",
  "nav.logout": "Logout",
  "nav.language": "Language",
  "btn.save": "Save",
  "btn.retry": "Try again",
  "btn.continue": "Continue",
  "error.offline_title": "You seem offline",
  "error.offline_body": "When connection returns, continue from where you left. A short breath now also helps.",
  "error.generic": "Something did not land. You may try again in a moment.",
  "error.unauthorized": "Sign in to continue your journey across devices.",
  "notifications.title": "Gentle Reminders",
  "notifications.daily": "Daily sādhana reminder",
  "notifications.festival": "Festival reminders (Ekādaśī, Pūrṇimā)",
  "notifications.streak": "Sādhana streak encouragement",
  "tri.english": "English",
  "tri.preferred": "Your language",
  "tri.settings_title": "Language & meanings",
  "tri.settings_hint": "Sanskrit and English are always available. Choose your preferred language for meanings.",
  "tri.show_english": "Show English meaning",
  "tri.show_sanskrit": "Show Sanskrit & transliteration",
  "tri.indian_languages": "Indian languages",
  "tri.global_languages": "Global languages",
  "menu.global_chanting": "Global Chanting",
  "btn.join_now": "Join Now",
  "btn.listen": "Listen",
};

const hi = {
  ...en,
  "nav.knowledge": "ज्ञान कोष",
  "nav.library": "पवित्र पुस्तकालय",
  "nav.panchanga": "पञ्चाङ्ग",
  "nav.prayer_hall": "ध्यान मन्दिर",
  "nav.satsanga": "सत्सङ्ग",
  "nav.sadhana": "साधना",
  "nav.sign_in": "प्रवेश",
  "nav.logout": "निर्गम",
  "nav.language": "भाषा",
  "nav.crisis": "संकट मार्गदर्शन",
};

const ta = {
  ...en,
  "nav.knowledge": "அறிவுக் களஞ்சியம்",
  "nav.library": "புனித நூலகம்",
  "nav.panchanga": "பஞ்சாங்கம்",
  "nav.prayer_hall": "தியான மந்திரம்",
  "nav.sadhana": "சாதனை",
};

const te = {
  ...en,
  "nav.knowledge": "జ్ఞాన భండారం",
  "nav.panchanga": "పంచాంగం",
  "nav.prayer_hall": "ధ్యాన మందిరం",
};

const kn = { ...en, "nav.panchanga": "ಪಂಚಾಂಗ", "nav.prayer_hall": "ಧ್ಯಾನ ಮಂದಿರ" };
const ml = { ...en, "nav.panchanga": "പഞ്ചാംഗം", "nav.prayer_hall": "ധ്യാന മന്ദിരം" };
const sa = { ...en, "nav.panchanga": "पञ्चाङ्गम्", "nav.prayer_hall": "ध्यानमन्दिरम्" };

export const LOCALES = { en, hi, ta, te, kn, ml, sa };

export function normalizeLang(code) {
  return normalizePreferredLang(code);
}

export function detectBrowserLang() {
  if (typeof navigator === "undefined") return "en";
  const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const l of langs) {
    const n = normalizeLang(l);
    if (n !== "en" || l?.toLowerCase().startsWith("en")) return n;
  }
  return "en";
}

export function translate(lang, key) {
  const dict = LOCALES[normalizeLang(lang)] ?? LOCALES.en;
  return dict[key] ?? LOCALES.en[key] ?? key;
}
